import { useState, useEffect } from 'react';
import { ShieldAlert, ExternalLink, Check } from 'lucide-react';

export function WelcomeModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);

  useEffect(() => {
    // Check if user has already accepted the disclaimer
    const accepted = localStorage.getItem('disclaimerAccepted');
    if (!accepted) {
      setIsOpen(true);
    }
  }, []);

  const handleAccept = () => {
    if (!understood) return;
    localStorage.setItem('disclaimerAccepted', 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0B] shadow-2xl animate-in fade-in zoom-in duration-300">
        
        {/* Glow effect */}
        <div className="absolute -top-32 -left-32 h-64 w-64 rounded-full bg-rose-500/10 blur-[100px]" />
        
        <div className="relative p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-500/20 text-rose-500 border border-rose-500/30">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-white">
              Before you continue
            </h2>
          </div>

          <div className="space-y-4 text-sm text-gray-300 leading-relaxed mb-8">
            <p>
              <strong className="text-white">thescope.fun</strong> is an on-chain analytics and research tool. Nothing on this site is financial, investment, or trading advice.
            </p>
            
            <p>
              Every verdict, score and signal (Bundled, Cabaled, Organic and the numbers behind them) is an automated, algorithmic opinion built from public blockchain data — a probabilistic estimate, not a factual or legal determination. It can be wrong, incomplete, or out of date, and no outcome is guaranteed. Memecoins are extremely risky: never commit money you cannot afford to lose, never treat a verdict as financial advice or as the sole basis for a decision, and always do your own research.
            </p>
            
            <p>
              <strong className="text-white">Cookies:</strong> essential cookies — your login session and this acceptance — plus Google Analytics, which counts visits and pages viewed. Analytics starts only after you accept below, never before, and we do not send Google the token addresses you look up. No ad-tracking — but we do load web fonts, a bot-check widget, and token images from their own providers as part of showing the page.
            </p>
            
            <p className="flex items-center gap-2 text-white/70">
              Read the full 
              <a href="https://thescope.fun/privacy" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 transition-colors">
                Privacy Policy <ExternalLink className="h-3 w-3" />
              </a>
              and 
              <a href="https://thescope.fun/terms" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 transition-colors">
                Terms of Service <ExternalLink className="h-3 w-3" />
              </a>
              — both open in a new tab so you keep your place here.
            </p>
          </div>

          <div className="bg-black/40 border border-white/5 rounded-xl p-4 mb-6">
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center mt-0.5">
                <input 
                  type="checkbox" 
                  className="peer sr-only"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                />
                <div className="h-5 w-5 rounded border border-white/20 bg-black/50 transition-all peer-checked:bg-emerald-500 peer-checked:border-emerald-500 group-hover:border-white/40"></div>
                <Check className="absolute h-3.5 w-3.5 text-white opacity-0 transition-opacity peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
              </div>
              <span className="text-sm font-medium text-white/90 select-none group-hover:text-white transition-colors">
                I understand thescope.fun provides algorithmic opinions, not financial advice — verdicts may be inaccurate, this is not a way to make money, and I use it entirely at my own risk.
              </span>
            </label>
          </div>

          <button
            onClick={handleAccept}
            disabled={!understood}
            className={`w-full py-4 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              understood 
                ? "bg-emerald-500 text-white hover:bg-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:scale-[1.02] active:scale-[0.98]" 
                : "bg-white/5 text-white/30 cursor-not-allowed"
            }`}
          >
            I agree — enter thescope.fun
          </button>
        </div>
      </div>
    </div>
  );
}
